/**
 * Conditions Builder Component
 * Allows users to build complex rule conditions with AND/OR logic
 */

import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Plus, Trash2 } from '@/components/icons'
import { cn } from '@/lib/utils'
import type {
  Condition,
  ConditionsGroup,
  ConditionField,
  ConditionOperator,
  ConditionInputType,
  FieldMetadata,
  OperatorMetadata,
} from './types'

interface ConditionsBuilderProps {
  conditions: ConditionsGroup
  onChange: (conditions: ConditionsGroup) => void
  className?: string
  fieldMetadata: Record<string, FieldMetadata>
  operatorMetadata: Record<string, OperatorMetadata>
}

export function ConditionsBuilder({
  conditions,
  onChange,
  className,
  fieldMetadata,
  operatorMetadata,
}: ConditionsBuilderProps) {
  const handleOperatorChange = (operator: 'AND' | 'OR') => {
    onChange({
      ...conditions,
      operator,
    })
  }

  const handleAddCondition = () => {
    const firstField = Object.keys(fieldMetadata)[0] as ConditionField || 'title'
    const fieldMeta = fieldMetadata[firstField]
    const defaultOperator = fieldMeta?.operators[0] || 'contains'
    const opMeta = operatorMetadata[defaultOperator]
    const inputType = opMeta?.inputType || 'text'
    const initialValues: string[] = inputType === 'number' ? ['0'] : []

    const newCondition: Condition = {
      field: firstField,
      operator: defaultOperator as ConditionOperator,
      values: initialValues,
    }
    onChange({
      ...conditions,
      conditions: [...conditions.conditions, newCondition],
    })
  }

  const handleRemoveCondition = (index: number) => {
    const newConditions = conditions.conditions.filter((_, i) => i !== index)
    onChange({
      ...conditions,
      conditions: newConditions,
    })
  }

  const handleConditionChange = (index: number, condition: Condition) => {
    const newConditions = [...conditions.conditions]
    newConditions[index] = condition
    onChange({
      ...conditions,
      conditions: newConditions,
    })
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* Conditions List */}
      {conditions.conditions.map((condition, index) => (
        <ConditionRow
          key={index}
          condition={condition}
          onChange={(newCondition) => handleConditionChange(index, newCondition)}
          onRemove={() => handleRemoveCondition(index)}
          showRemove={conditions.conditions.length > 1 || index > 0}
          index={index}
          operator={conditions.operator}
          onOperatorChange={handleOperatorChange}
          fieldMetadata={fieldMetadata}
          operatorMetadata={operatorMetadata}
        />
      ))}

      {/* Add Condition Button */}
      <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-2">
        <div className="w-20 md:w-20 flex-shrink-0"></div>
        <Button
          type="button"
          variant="outline"
          onClick={handleAddCondition}
          className="w-full md:w-[140px] h-9"
        >
          <Plus className="h-4 w-4" />
          Add Condition
        </Button>
      </div>
    </div>
  )
}

interface ConditionRowProps {
  condition: Condition
  onChange: (condition: Condition) => void
  onRemove: () => void
  showRemove: boolean
  index: number
  operator: 'AND' | 'OR'
  onOperatorChange: (operator: 'AND' | 'OR') => void
  fieldMetadata: Record<string, FieldMetadata>
  operatorMetadata: Record<string, OperatorMetadata>
}

function ConditionRow({
  condition,
  onChange,
  onRemove,
  showRemove,
  index,
  operator,
  onOperatorChange,
  fieldMetadata: fieldsMetadata,
  operatorMetadata: operatorsMetadata,
}: ConditionRowProps) {
  const isFirst = index === 0
  const isSecond = index === 1
  const fieldMetadata = fieldsMetadata[condition.field]
  const operatorMetadata = operatorsMetadata[condition.operator]
  const inputType: ConditionInputType = operatorMetadata?.inputType || 'text'

  const handleFieldChange = (field: ConditionField) => {
    const newFieldMetadata = fieldsMetadata[field]
    if (!newFieldMetadata) return

    const defaultOperator = newFieldMetadata.operators[0]
    const opMeta = operatorsMetadata[defaultOperator]
    const newInputType = opMeta?.inputType || 'text'
    const initialValues: string[] = newInputType === 'number' ? ['0'] : []

    onChange({
      field,
      operator: defaultOperator,
      values: initialValues,
    })
  }

  const handleConditionOperatorChange = (op: ConditionOperator) => {
    const targetOpMeta = operatorsMetadata[op]
    const targetInputType: ConditionInputType = targetOpMeta?.inputType || 'text'

    if (targetInputType === 'none') {
      onChange({ ...condition, operator: op, values: [] })
      return
    }

    const isSingleValue = targetInputType === 'text' || targetInputType === 'number' || targetInputType === 'time'
    const trimmedValues: string[] = isSingleValue
      ? condition.values.length
        ? [condition.values[0]]
        : targetInputType === 'number'
          ? ['0']
          : []
      : condition.values

    onChange({
      ...condition,
      operator: op,
      values: trimmedValues,
    })
  }

  const handleValuesChange = (values: string[]) => {
    onChange({
      ...condition,
      values,
    })
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-2">
        {/* Where/And/Or Selector */}
        <div className="w-20 md:w-20 flex-shrink-0">
          {isFirst ? (
            <span className="text-sm font-medium whitespace-nowrap h-9 flex items-center">Condition</span>
          ) : isSecond ? (
            <Select
              value={operator}
              onValueChange={(value) => onOperatorChange(value as 'AND' | 'OR')}
            >
              <SelectTrigger className="w-20 md:w-20 shadow-none h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AND">And</SelectItem>
                <SelectItem value="OR">Or</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <span className="text-sm font-medium whitespace-nowrap h-9 flex items-center capitalize">{operator.toLowerCase()}</span>
          )}
        </div>

        {/* Field Selector with Delete Button (Mobile) */}
        <div className="flex items-center gap-2 flex-1 md:flex-initial md:w-[140px] w-full">
          <div className="flex-1 min-w-0">
            <Select
              value={condition.field}
              onValueChange={(value) => handleFieldChange(value as ConditionField)}
            >
              <SelectTrigger className="shadow-none h-9 w-full md:w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(fieldsMetadata).map((field) => (
                  <SelectItem key={field.field} value={field.field}>
                    {field.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Delete Button - Mobile only (next to field dropdown, aligned right) */}
          {showRemove && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRemove}
              className="h-9 px-2 text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0 md:hidden"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Operator Selector */}
        <Select
          value={condition.operator}
          onValueChange={(value) =>
            handleConditionOperatorChange(value as ConditionOperator)
          }
        >
          <SelectTrigger className="shadow-none h-9 flex-1 md:flex-initial md:w-[120px] w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {fieldMetadata?.operators?.map((op) => (
              <SelectItem key={op} value={op}>
                {operatorsMetadata[op]?.label || op}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Value Input */}
        <div className="flex-1 w-full min-w-0">
          <ConditionValueInput
            values={condition.values}
            onChange={handleValuesChange}
            inputType={inputType}
            field={condition.field}
          />
        </div>

        {/* Delete Button - Desktop only (after values field) */}
        {showRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="h-9 px-2 text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0 hidden md:flex"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      
      {/* Operator Description */}
      {fieldMetadata?.operatorDetails?.[condition.operator] && (
        <div className="px-0.5 md:ml-[88px]">
          <p className="text-xs text-muted-foreground">
            {fieldMetadata.operatorDetails[condition.operator].description}
          </p>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Unified value input component driven by operator input_type
// ============================================================================

interface ConditionValueInputProps {
  values: string[]
  onChange: (values: string[]) => void
  inputType: ConditionInputType
  field: string
}

function ConditionValueInput({ values, onChange, inputType, field }: ConditionValueInputProps) {
  switch (inputType) {
    case 'none':
      return null
    case 'time':
      return <TimeInput values={values} onChange={onChange} />
    case 'number':
      return <NumberInput values={values} onChange={onChange} field={field} />
    case 'array':
      return <ArrayInput values={values} onChange={onChange} />
    case 'text':
    default:
      return <TextInput values={values} onChange={onChange} />
  }
}

function TextInput({ values, onChange }: { values: string[]; onChange: (v: string[]) => void }) {
  const displayValue = values[0] || ''
  return (
    <Input
      type="text"
      value={displayValue}
      onChange={(e) => onChange([e.target.value])}
      placeholder="Value"
      className="shadow-none h-9"
    />
  )
}

function ArrayInput({ values, onChange }: { values: string[]; onChange: (v: string[]) => void }) {
  const [inputValue, setInputValue] = useState(values.join(', '))
  const isLocalChange = useRef(false)

  useEffect(() => {
    if (isLocalChange.current) {
      isLocalChange.current = false
      return
    }
    const newInputValue = values.join(', ')
    if (newInputValue !== inputValue) {
      setInputValue(newInputValue)
    }
  }, [values]) // eslint-disable-line react-hooks/exhaustive-deps

  const parseInput = (raw: string): string[] =>
    raw.split(',').map((v) => v.trim()).filter((v) => v.length > 0)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    setInputValue(raw)
    isLocalChange.current = true
    onChange(parseInput(raw))
  }

  const handleBlur = () => {
    const parsed = parseInput(inputValue)
    isLocalChange.current = true
    onChange(parsed)
    setInputValue(parsed.join(', '))
  }

  return (
    <Input
      type="text"
      value={inputValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder="Value1, Value2, Value3"
      className="shadow-none h-9"
    />
  )
}

function NumberInput({ values, onChange, field }: { values: string[]; onChange: (v: string[]) => void; field: string }) {
  const numericValue = Number(values[0]) || 0

  if (field === 'scheduled_duration') {
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={0}
          value={numericValue || ''}
          onChange={(e) => {
            const parsed = parseInt(e.target.value, 10)
            onChange([String(isNaN(parsed) ? 0 : parsed)])
          }}
          placeholder="Minutes"
          className="shadow-none h-9"
        />
        <span className="text-sm text-muted-foreground whitespace-nowrap">min</span>
      </div>
    )
  }

  const numberOptions = Array.from({ length: 11 }, (_, i) => i)
  return (
    <Select
      value={String(numericValue)}
      onValueChange={(val) => onChange([val])}
    >
      <SelectTrigger className="shadow-none h-9">
        <SelectValue placeholder="Select number" />
      </SelectTrigger>
      <SelectContent>
        {numberOptions.map((num) => (
          <SelectItem key={num} value={String(num)}>
            {num}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function TimeInput({ values, onChange }: { values: string[]; onChange: (v: string[]) => void }) {
  const timeValue = values[0] || ''
  return (
    <Input
      type="time"
      value={timeValue}
      onChange={(e) => onChange([e.target.value])}
      className="shadow-none h-9"
    />
  )
}
